/**
 * PoseStrip — the shared viewport card strip for any "named pose over a subtree"
 * system (Keyframes and Properties state groups).
 *
 * Extracted from mount-keyframe-context.tsx as the generic ~88% (card strip,
 * context menu + clamping, 300ms polling, status line, mount/ResizeObserver).
 * The two systems become thin wrappers that mount this with a small adapter:
 *   - Keyframes: passes a `thumbnailProvider` (its cache) + animated select.
 *   - State groups: passes `thumbnailProvider: null` (or a posed-capture source).
 *
 * Keyframe-specific lifecycle (live thumbnail capture, base-signature polling,
 * off-screen fill, transition timing) stays in the keyframe wrapper, which feeds
 * this component a ready `thumbnailProvider`. PoseStrip never imports Keyframes
 * or Properties — it is behavior-agnostic.
 */

import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { createRoot, type Root } from 'react-dom/client';
import type { SceneObject } from 'trace-viewer/scene';
import type { ContextCrumb } from 'trace-viewer/editor';
import { useDragReorder } from './components/use-drag-reorder';
import { ViewportContextFrame } from './components/viewport-context-frame';
import { observeInspectorPaneInsets } from './inspector-panes';

// ── Adapter ──────────────────────────────────────────────────

/**
 * One segment of the strip's hierarchy breadcrumb (see PoseAdapter.breadcrumb).
 * Shape is the shared `ContextCrumb` from trace-viewer/editor — the builder
 * (`buildContextBreadcrumb`) lives there so TraceFrontend shares it.
 */
export type PoseCrumb = ContextCrumb;

/** A thumbnail source for cards. Keyframes provide one; states may pass null. */
export interface PoseThumbnailProvider {
    /** Data URL for a pose's card, or undefined → placeholder. `null` = Base. */
    getThumbnail(name: string | null): string | undefined;
    /** Subscribe to thumbnail-cache changes; returns an unsubscribe. */
    subscribe(callback: () => void): () => void;
}

/**
 * What PoseStrip needs from a pose system. Pure data + actions — no React, no
 * behavior-class coupling. The `*` actions may be sync (states) or fire async
 * internally (keyframes); the strip re-reads names/active after each via polling.
 */
export interface PoseAdapter {
    /** The object owning the pose set (for re-render keying + display name). */
    owner: SceneObject;
    ownerName: string;
    /** Current pose names (excluding Base, which the strip always renders). */
    getNames(): string[];
    /** The active pose name, or null for Base. */
    getActive(): string | null;
    /** Select a pose (null = Base). `animate` is honoured by systems that support it. */
    select(name: string | null, animate: boolean): void;
    /** Add a new pose; the system selects it. */
    add(): void;
    /** Delete a pose. */
    remove(name: string): void;
    /** Rename a pose. */
    rename(oldName: string, newName: string): void;
    /** Duplicate a pose. */
    duplicate(name: string): void;
    /**
     * Optional drag-reorder of the pose cards (pages). Called with the
     * dragged card's index and the insertion slot (both in `getNames()`
     * space). Omit for systems whose order isn't positional (keyframes,
     * states).
     */
    reorder?: (from: number, slot: number) => void;
    /** Optional thumbnail source. null/undefined → name-only cards. */
    thumbnails?: PoseThumbnailProvider | null;
    /** Word for a pose in tooltips/labels (e.g. "keyframe", "state"). */
    noun?: string;
    /** Label for the Base card (default "Base"; pages use "All"). */
    baseLabel?: string;
    /** Tooltip for the Base card (default explains authored-scene editing). */
    baseTitle?: string;
    /**
     * Corner badge on the Base card (default "base"). Pass null to hide it —
     * pages hide it: their "All" card is an overview parent, not a base pose.
     */
    baseBadge?: string | null;
    /**
     * Draw a divider between the Base card and the pose cards. Pages set this:
     * "All" is a separate (virtual) parent context, not a sibling page.
     */
    baseSeparated?: boolean;
    /**
     * Optional BREADCRUMB — the hierarchy path around the selection (outermost
     * enclosing group → … → the selection leaf). One crumb `isContext`: the
     * object whose state group the cards/thumbnails/frame show. One crumb
     * `isSelection`: the edit subject (they may be the same object). Clicking a
     * crumb SELECTS that object (plain hierarchy selection — the context then
     * re-derives on its own); each crumb's ▾ lists its siblings for lateral
     * hops. Always rendered when provided, even with a single crumb, so the
     * panel never loses its navigation. States use this; keyframes omit it.
     */
    breadcrumb?: PoseCrumb[];
    /** Called when a crumb (or a sibling menu entry) is clicked — select it. */
    onSelectObject?: (object: SceneObject) => void;
    /**
     * Optional editing-mode gate. When defined, the strip is collapsible: `false`
     * shows only a compact toggle button (no cards, no routing), `true` shows the
     * full strip. Leave undefined for an always-on strip (keyframes). States pass
     * this so editing mode can be turned off, keeping edits on the authored scene.
     */
    enabled?: boolean;
    /** Called when the user flips the editing-mode toggle. */
    onToggleEnabled?: (enabled: boolean) => void;
    /**
     * Where the strip anchors within its container. Defaults to 'bottom-center'
     * (keyframes). States use 'bottom-left'. 'static' opts out of absolute
     * positioning entirely — the strip-tabs coordinator stacks the strip above
     * its persistent tab bar in a flex column and owns the placement.
     */
    corner?: 'bottom-center' | 'bottom-left' | 'top-center' | 'static';
    /**
     * Optional viewport "context frame" label — the edited root's name. The
     * strip portals the frame to the slot element (#state-context-container)
     * so it spans the full canvas area, not the strip's own box. Omit to draw
     * no frame (keyframes).
     */
    frameLabel?: string;
    /**
     * Optional frame-header NAVIGATION (pages): the frame header shows the
     * ACTIVE card name (falling back to baseLabel) flanked by ◀ ▶ buttons
     * wired to these handlers, instead of the static frameLabel.
     */
    frameNav?: { onPrev: () => void; onNext: () => void };
    /**
     * Optional footer row rendered INSIDE the panel below the cards — the
     * strip-tabs coordinator injects its persistent tab bar here so the panel
     * background encompasses the tabs.
     */
    footer?: React.ReactNode;
    /**
     * Optional "Independent" TOGGLE (states only). Acts on the SELECTION (the
     * subject), not the strip's context: it reflects whether the SELECTED object
     * owns an INDEPENDENT nested state group (`on`) versus joining the group it
     * sits inside (off). Toggling separates or re-joins that object. The label is
     * always just "Independent" — the subject is named by the breadcrumb's
     * selection crumb, so repeating it here is redundant. `subjectName` is used
     * only in the tooltip. `disabled` greys it out (with `disabledReason` as the
     * tooltip) when the action isn't currently applicable. Omit where the concept
     * doesn't apply (keyframes).
     */
    separateStates?: {
        on: boolean;
        subjectName?: string;
        disabled?: boolean;
        disabledReason?: string;
        onToggle: (next: boolean) => void;
    } | null;
    /**
     * Optional PIN toggle (pages only) — a thumbtack at the start of the
     * selection tab. Acts on the SELECTION: for pages it reflects whether the
     * selected top-level object is SHARED across pages (pinned) and toggling
     * shares/unshares it (both undoable). Omit where the concept doesn't apply.
     */
    pin?: {
        on: boolean;
        title?: string;
        onToggle: (next: boolean) => void;
    } | null;
}

// ── Design tokens ────────────────────────────────────────────
// Context = cool cyan-blue (the group whose cards show). Selection = warm coral
// (the edit subject). The two roles users conflate, kept legible by temperature —
// coral over the blue-tinted panel stays a clean warm (a low-saturation amber went
// muddy brown against it). rgb triples so alpha applies per use; CONTEXT matches
// ViewportContextFrame. The *_INK values are the tab label colors (bright tints of
// each accent) so text stays readable on the translucent fills.
const CONTEXT = '120, 170, 210';
const CONTEXT_INK = '#dbe9f5';
const SELECTION = '236, 142, 118';
const SELECTION_INK = '#fbe0d6';
const INK = 'rgba(236, 240, 245, 0.95)';
const INK_DIM = 'rgba(236, 240, 245, 0.55)';
const INK_FAINT = 'rgba(236, 240, 245, 0.32)';
const HAIRLINE = 'rgba(255, 255, 255, 0.10)';
const HAIRLINE_STRONG = 'rgba(255, 255, 255, 0.16)';

// ── Styles (from the keyframe strip; shared verbatim) ────────

const styles = {
    card: {
        position: 'relative' as const,
        width: '92px',
        minWidth: '92px',
        height: '68px',
        borderRadius: '8px',
        overflow: 'hidden',
        cursor: 'pointer',
        border: `1.5px solid ${HAIRLINE}`,
        backgroundColor: '#1e1e1e',
        backgroundImage: 'linear-gradient(#262b33, #191d24)',
        flexShrink: 0,
        transition: 'border-color 0.12s, box-shadow 0.12s, transform 0.1s',
    } as React.CSSProperties,
    cardSelected: {
        borderColor: `rgb(${CONTEXT})`,
        boxShadow: `0 0 0 1px rgb(${CONTEXT}), 0 4px 14px rgba(${CONTEXT}, 0.35)`,
    } as React.CSSProperties,
    cardImage: { width: '100%', height: '100%', objectFit: 'cover' as const, display: 'block' } as React.CSSProperties,
    cardPlaceholder: { width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: INK_FAINT, fontSize: '20px' } as React.CSSProperties,
    cardLabel: { position: 'absolute' as const, left: 0, right: 0, bottom: 0, padding: '12px 7px 5px', fontSize: '10px', color: INK, background: 'linear-gradient(transparent, rgba(0, 0, 0, 0.82))', whiteSpace: 'nowrap' as const, overflow: 'hidden', textOverflow: 'ellipsis', pointerEvents: 'none' as const } as React.CSSProperties,
    baseBadge: { position: 'absolute' as const, top: '4px', left: '4px', fontSize: '8px', letterSpacing: '0.06em', textTransform: 'uppercase' as const, color: INK_DIM, backgroundColor: 'rgba(0, 0, 0, 0.55)', borderRadius: '3px', padding: '1px 4px' } as React.CSSProperties,
    addCard: { width: '46px', minWidth: '46px', height: '68px', borderRadius: '8px', border: `1.5px dashed ${HAIRLINE_STRONG}`, backgroundColor: 'rgba(255, 255, 255, 0.015)', color: INK_DIM, cursor: 'pointer', fontSize: '20px', flexShrink: 0, transition: 'color 0.12s, border-color 0.12s, background-color 0.12s' } as React.CSSProperties,
    deleteButton: { position: 'absolute' as const, top: '3px', right: '3px', padding: '0 4px', backgroundColor: 'rgba(0, 0, 0, 0.6)', border: 'none', borderRadius: '3px', color: '#ccc', cursor: 'pointer', fontSize: '12px', lineHeight: '15px', transition: 'opacity 0.1s' } as React.CSSProperties,
    renameInput: { position: 'absolute' as const, left: '3px', right: '3px', bottom: '3px', backgroundColor: 'rgba(20, 24, 30, 0.96)', border: `1px solid rgb(${CONTEXT})`, borderRadius: '3px', color: '#fff', padding: '1px 4px', fontSize: '10px', outline: 'none', minWidth: 0 } as React.CSSProperties,
};

// Breadcrumb chrome — nested arrow tabs. Each tab is a chevron-shaped segment
// (clip-path notch on the left, point on the right); segments overlap and
// interlock. The first tab has a flat left edge so it seats against the panel.
const TAB_MID_CLIP = 'polygon(0 0, calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 0 100%, 10px 50%)';
const TAB_FIRST_CLIP = 'polygon(0 0, calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 0 100%)';

const chrome = {
    tab: {
        position: 'relative' as const,
        display: 'inline-flex' as const, alignItems: 'center' as const,
        height: '26px', padding: '0 12px 0 20px', marginLeft: '-10px',
        fontSize: '12px', fontWeight: 500, letterSpacing: '0.01em',
        color: INK_DIM, cursor: 'pointer', whiteSpace: 'nowrap' as const,
        maxWidth: '160px', overflow: 'hidden',
        background: 'rgba(255, 255, 255, 0.05)', clipPath: TAB_MID_CLIP,
        transition: 'background-color 0.12s, color 0.12s',
    } as React.CSSProperties,
    tabFirst: {
        marginLeft: 0, paddingLeft: '13px', clipPath: TAB_FIRST_CLIP,
        borderRadius: '7px 0 0 7px',
    } as React.CSSProperties,
    tabLabel: { overflow: 'hidden', textOverflow: 'ellipsis' } as React.CSSProperties,
    // Ghost "peek ahead" tab — visual rim/hover live in .pose-ghost (style.css);
    // these are the extras CSS can't carry: a width cap for long child names.
    ghost: { maxWidth: '140px', overflow: 'hidden' } as React.CSSProperties,
    ghostLabel: { overflow: 'hidden', textOverflow: 'ellipsis' } as React.CSSProperties,
    // Independent toggle — the link icon at the START of the selection tab.
    link: {
        display: 'inline-flex' as const, alignItems: 'center' as const, justifyContent: 'center' as const,
        width: '17px', height: '17px', marginRight: '5px', borderRadius: '4px',
        flexShrink: 0, opacity: 0.75, transition: 'opacity 0.12s, background-color 0.12s',
    } as React.CSSProperties,
    close: {
        display: 'inline-flex' as const, alignItems: 'center' as const, justifyContent: 'center' as const,
        width: '22px', height: '22px', borderRadius: '6px', marginLeft: 'auto',
        color: INK_FAINT, cursor: 'pointer', border: '1px solid transparent', background: 'none',
        fontSize: '13px', flexShrink: 0, transition: 'color 0.12s, background-color 0.12s',
    } as React.CSSProperties,
};

// Thumbtack (pin) glyph for the shared-across-pages toggle. Filled head when
// pinned; outline when not. `currentColor` inherits the tab/menu row color.
function PinGlyph({ on }: { on: boolean }) {
    return (
        <svg width={11} height={11} viewBox="0 0 24 24" fill={on ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
            <path d="M12 17v5" />
            <path d="M9 3h6l-1 7 3 3H7l3-3z" />
        </svg>
    );
}

// Link (joined) / broken-link (independent) glyphs. Inline SVG — emoji link
// glyphs render inconsistently across platforms. `currentColor` inherits the
// tab's color so the amber "independent" state comes for free.
function LinkGlyph({ broken }: { broken: boolean }) {
    const common = { width: 12, height: 12, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, style: { display: 'block' } };
    return broken ? (
        <svg {...common}>
            <path d="M15.5 12.5 18 10a5 5 0 0 0-7-7l-1.5 1.5" />
            <path d="M8.5 11.5 6 14a5 5 0 0 0 7 7l1.5-1.5" />
            <path d="M19 15h3M2 9h3M15 19v3M9 2v3" />
        </svg>
    ) : (
        <svg {...common}>
            <path d="M10 13a5 5 0 0 0 7 0l2-2a5 5 0 0 0-7-7l-1 1" />
            <path d="M14 11a5 5 0 0 0-7 0l-2 2a5 5 0 0 0 7 7l1-1" />
        </svg>
    );
}

// ── Portal menu helper ───────────────────────────────────────

/**
 * Shared lifecycle for a small portal popup menu opened at a screen point:
 * dismiss on outside pointer-down / Escape / wheel (deferred attach so the
 * opening click doesn't immediately close it), and clamp inside the viewport
 * once measured (no visible jump). Used by the card context menu and the
 * breadcrumb sibling menus.
 */
function usePortalMenu(
    at: { x: number; y: number } | null,
    ref: React.RefObject<HTMLDivElement | null>,
    close: () => void,
): { left: number; top: number } | null {
    const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

    useEffect(() => {
        if (!at) return;
        const onPointerDown = (e: PointerEvent) => {
            if (ref.current?.contains(e.target as Node)) return;
            close();
        };
        const onWheel = () => close();
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
        const id = setTimeout(() => {
            window.addEventListener('pointerdown', onPointerDown);
            window.addEventListener('wheel', onWheel, { passive: true });
        }, 0);
        window.addEventListener('keydown', onKey);
        return () => {
            clearTimeout(id);
            window.removeEventListener('pointerdown', onPointerDown);
            window.removeEventListener('wheel', onWheel);
            window.removeEventListener('keydown', onKey);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [at]);

    useLayoutEffect(() => {
        if (!at) { setPos(null); return; }
        const M = 6;
        const rect = ref.current?.getBoundingClientRect();
        const w = rect?.width ?? 0;
        const h = rect?.height ?? 0;
        setPos({
            left: Math.max(M, Math.min(at.x, window.innerWidth - w - M)),
            top: Math.max(M, Math.min(at.y, window.innerHeight - h - M)),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [at]);

    return pos;
}

// ── Strip component ──────────────────────────────────────────

export function PoseStrip({ adapter }: { adapter: PoseAdapter }) {
    const noun = adapter.noun ?? 'pose';
    const [names, setNames] = useState<string[]>(() => adapter.getNames());
    const [active, setActive] = useState<string | null>(() => adapter.getActive());
    const [ownerName, setOwnerName] = useState(adapter.ownerName);
    const [hovered, setHovered] = useState<string | null>(null);
    const [renaming, setRenaming] = useState<string | null>(null);
    const [draft, setDraft] = useState('');
    const [menu, setMenu] = useState<{ name: string; x: number; y: number } | null>(null);
    const [crumbMenu, setCrumbMenu] = useState<{ index: number; kind: 'siblings' | 'children'; x: number; y: number } | null>(null);
    const [, setThumbVersion] = useState(0);
    const menuRef = useRef<HTMLDivElement>(null);
    const crumbMenuRef = useRef<HTMLDivElement>(null);
    const menuPos = usePortalMenu(menu, menuRef, useCallback(() => setMenu(null), []));
    const crumbMenuPos = usePortalMenu(crumbMenu, crumbMenuRef, useCallback(() => setCrumbMenu(null), []));

    // Poll the system for names/active/owner-name (commits reset the active
    // stack; rename-in-place changes the owner name). 300ms matches the keyframe
    // strip. The adapter actions may fire async, so polling reconciles.
    useEffect(() => {
        const poll = () => {
            setNames(adapter.getNames());
            setActive(adapter.getActive());
            setOwnerName(adapter.owner.name);
        };
        poll();
        const id = setInterval(poll, 300);
        return () => clearInterval(id);
    }, [adapter]);

    // Re-render when thumbnails land; no-op when no provider. `adapter.thumbnails`
    // is a stable value per adapter (a fresh adapter is built whenever it would
    // change), so the [adapter] dependency covers (re)subscription. A capture's
    // notify() is dropped if it lands while NO strip is subscribed — e.g. the strip
    // remounts (selection blip / behavior add) during the serialized capture queue,
    // so the in-flight card's notify fires with zero listeners and that card stays
    // a placeholder until something else re-renders (the "blank until reselect"
    // symptom). Bump once on (re)subscribe to re-read any thumbnail that landed
    // during the no-subscriber window.
    useEffect(() => {
        const provider = adapter.thumbnails;
        if (!provider) return;
        const unsub = provider.subscribe(() => setThumbVersion((v) => v + 1));
        setThumbVersion((v) => v + 1);
        return unsub;
    }, [adapter]);

    const refreshSoon = useCallback(() => {
        // Read once immediately so add/delete feel instant before the next poll.
        setNames(adapter.getNames());
        setActive(adapter.getActive());
    }, [adapter]);

    // Optional card drag-reorder (pages). The hook is unconditional (rules of
    // hooks); the drag props only spread when the adapter opts in.
    const canReorder = !!adapter.reorder;
    const onCardReorder = useCallback((from: number, slot: number) => {
        adapter.reorder?.(from, slot);
        refreshSoon();
    }, [adapter, refreshSoon]);
    const { dragIndex, dropSlot, getContainerProps, getDraggableProps } =
        useDragReorder(names.length, onCardReorder, { horizontal: true });

    const selectPose = (name: string | null) => {
        adapter.select(name, true);
        setActive(name);
    };
    const onAdd = () => { adapter.add(); refreshSoon(); };
    const onDelete = (name: string, e?: React.MouseEvent) => { e?.stopPropagation(); adapter.remove(name); refreshSoon(); };
    const onDuplicate = (name: string) => { adapter.duplicate(name); refreshSoon(); };

    const startRename = (name: string) => { setRenaming(name); setDraft(name); };
    const commitRename = (oldName: string) => {
        const next = draft.trim();
        setRenaming(null);
        if (!next || next === oldName) return;
        adapter.rename(oldName, next);
        refreshSoon();
    };

    const capturing = active !== null;
    const thumb = (name: string | null): string | undefined => adapter.thumbnails?.getThumbnail(name);

    // Collapsible editing mode (states). When the adapter opts into a gate and it
    // is off, render only a compact "turn on" button — no cards, no routing.
    const gated = adapter.enabled !== undefined;
    const collapsed = gated && !adapter.enabled;

    // Anchor within the inter-pane container. Default centered (keyframes);
    // states sit bottom-left; 'static' participates in the caller's flex layout
    // (the strip-tabs coordinator stacks the strip above its tab bar).
    const anchor: React.CSSProperties = adapter.corner === 'bottom-left'
        ? { position: 'absolute', bottom: '12px', left: '12px' }
        : adapter.corner === 'top-center'
            ? { position: 'absolute', top: '12px', left: '50%', transform: 'translateX(-50%)' }
            : adapter.corner === 'static'
                ? { position: 'relative' }
                : { position: 'absolute', bottom: '12px', left: '50%', transform: 'translateX(-50%)' };

    if (collapsed) {
        return (
            <div
                style={{
                    ...anchor,
                    pointerEvents: 'auto', userSelect: 'none',
                }}
            >
                <button
                    className="pose-edit"
                    onClick={() => adapter.onToggleEnabled?.(true)}
                    title={`Turn on ${noun} editing for "${ownerName}" — edit and preview ${noun}s in the viewport. While off, edits change the base scene.`}
                    style={{
                        display: 'flex', alignItems: 'center', gap: '7px',
                        padding: '7px 13px', borderRadius: '9px',
                        background: 'rgba(18, 20, 26, 0.92)',
                        border: `1px solid ${HAIRLINE}`,
                        color: INK_DIM, cursor: 'pointer', fontSize: '11px',
                        backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
                        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.4)',
                        transition: 'color 0.12s, border-color 0.12s',
                    }}
                >
                    <span style={{ fontSize: '12px', color: `rgb(${CONTEXT})` }}>◇</span>
                    Edit {noun}s
                </button>
            </div>
        );
    }

    // Viewport context frame — portaled to the slot element so it spans the
    // full canvas area between the panes (the strip itself sits inside the
    // bottom-left column, which would clip it). With frameNav (pages), the
    // header shows the live ACTIVE card name flanked by prev/next.
    const frameHost = typeof document !== 'undefined' ? document.getElementById('state-context-container') : null;
    const frame = adapter.frameLabel && frameHost && createPortal(
        <ViewportContextFrame
            label={adapter.frameNav ? (active ?? adapter.baseLabel ?? 'Base') : adapter.frameLabel}
            suffix={`· editing ${noun}s`}
            nav={adapter.frameNav && {
                onPrev: () => { adapter.frameNav!.onPrev(); refreshSoon(); },
                onNext: () => { adapter.frameNav!.onNext(); refreshSoon(); },
                prevTitle: `Previous ${noun}`,
                nextTitle: `Next ${noun}`,
            }}
        />,
        frameHost,
    );

    return (
        <>
            {frame}
            <div
            style={{
                ...anchor,
                maxWidth: adapter.corner === 'static' ? '100%' : 'calc(100% - 32px)',
                display: 'flex', flexDirection: 'column', gap: '7px',
                padding: '8px 10px 9px', borderRadius: '12px',
                background: capturing ? 'rgba(20, 30, 46, 0.94)' : 'rgba(18, 20, 26, 0.94)',
                border: `1px solid ${capturing ? `rgba(${CONTEXT}, 0.32)` : HAIRLINE}`,
                boxShadow: '0 8px 28px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.04)',
                backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
                color: INK, pointerEvents: 'auto', userSelect: 'none',
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {/* Breadcrumb as nested arrow tabs — the hierarchy path around the
                    selection. Each tab is a whole clickable chevron segment: the
                    COOL tab is the context (its states are the cards below), the
                    WARM tab is the selection leaf (edit subject); they may coincide.
                    Clicking an ancestor tab SELECTS that object. Clicking the
                    SELECTION LEAF opens its sibling menu (lateral hop) — the leaf
                    is already selected, so a click there is most useful for moving
                    sideways; no separate ⌄ affordance. The Independent toggle is
                    the link icon at the START of the selection tab — it acts on
                    that object. Always shown, even with one tab. */}
                {adapter.breadcrumb && adapter.breadcrumb.length > 0 && adapter.onSelectObject ? (
                    <div style={{ display: 'flex', alignItems: 'stretch', minWidth: 0, overflow: 'hidden', whiteSpace: 'nowrap' }}>
                        {adapter.breadcrumb.map((c, i) => {
                            // Base colors are dynamic (context vs selection accent);
                            // the hover DELTA is CSS (.pose-tab:hover) reading the
                            // --tab-hover-* props set here — so a re-render while the
                            // pointer is stationary can't strand the hover style.
                            const tabStyle: React.CSSProperties & Record<string, string | number> = {
                                ...chrome.tab, ...(i === 0 ? chrome.tabFirst : {}),
                                zIndex: adapter.breadcrumb!.length - i,
                                '--tab-hover-bg': 'rgba(255, 255, 255, 0.11)',
                                '--tab-hover-fg': INK,
                            };
                            if (c.isSelection) {
                                tabStyle.background = `rgba(${SELECTION}, 0.22)`;
                                tabStyle.color = SELECTION_INK;
                                tabStyle.fontWeight = 600;
                                tabStyle['--tab-hover-bg'] = `rgba(${SELECTION}, 0.34)`;
                                tabStyle['--tab-hover-fg'] = SELECTION_INK;
                            } else if (c.isContext) {
                                tabStyle.background = `rgba(${CONTEXT}, 0.30)`;
                                tabStyle.color = CONTEXT_INK;
                                tabStyle.fontWeight = 600;
                                tabStyle['--tab-hover-bg'] = `rgba(${CONTEXT}, 0.42)`;
                                tabStyle['--tab-hover-fg'] = CONTEXT_INK;
                            }
                            // The selection LEAF hops to a sibling on click (if it has
                            // any); ancestor tabs select. Only the leaf carries the
                            // sibling affordance, so a mid-path object's siblings are
                            // never offered from an inner crumb.
                            const leafHops = c.isSelection && c.siblings.length > 0;
                            // The Independent link toggle — only on the selection tab,
                            // and only when the adapter provides the toggle (states).
                            const sep = c.isSelection ? adapter.separateStates : null;
                            // The shared thumbtack — selection tab only (pages).
                            const pin = c.isSelection ? adapter.pin : null;
                            // Ghost "peek ahead" tab — the selection leaf's direct
                            // children (buildContextBreadcrumb fills `children` only on
                            // the subject). One child → its name, click selects it;
                            // several → "{n} children", click opens the child dropdown.
                            // Picking a child selects it, the breadcrumb rebuilds, and a
                            // fresh ghost appears if THAT child has children — descend
                            // hop-by-hop, all from the derivation (no descend state).
                            const kids = c.isSelection ? c.children : [];
                            const oneKid = kids.length === 1;
                            return (
                                <React.Fragment key={i}>
                                    <span
                                        className="pose-tab"
                                        onClick={(e) => {
                                            if (leafHops) setCrumbMenu({ index: i, kind: 'siblings', x: e.clientX, y: e.clientY });
                                            else adapter.onSelectObject?.(c.object);
                                        }}
                                        title={
                                            c.isSelection
                                                ? leafHops
                                                    ? `"${c.label}" — selected. Click to switch to a sibling.`
                                                    : `"${c.label}" — the selected object`
                                                : c.isContext
                                                    ? `"${c.label}" — the state group shown below. Click to select it`
                                                    : `Select "${c.label}"`
                                        }
                                        style={tabStyle}
                                    >
                                        {sep && (
                                            <span
                                                className={sep.disabled ? undefined : 'pose-link'}
                                                role="switch"
                                                aria-checked={sep.on}
                                                onClick={(e) => { e.stopPropagation(); if (!sep.disabled) sep.onToggle(!sep.on); }}
                                                title={
                                                    sep.disabled && sep.disabledReason
                                                        ? sep.disabledReason
                                                        : sep.on
                                                            ? `"${sep.subjectName ?? c.label}" is independent — it owns its own set of states. Click to rejoin the group it sits inside.`
                                                            : `Make "${sep.subjectName ?? c.label}" independent — give it its own set of states, separate from the group it sits inside.`
                                                }
                                                style={{
                                                    ...chrome.link,
                                                    cursor: sep.disabled ? 'default' : 'pointer',
                                                    opacity: sep.disabled ? 0.4 : sep.on ? 1 : 0.7,
                                                    color: sep.on ? `rgb(${SELECTION})` : 'inherit',
                                                }}
                                            >
                                                <LinkGlyph broken={sep.on} />
                                            </span>
                                        )}
                                        {pin && (
                                            <span
                                                className="pose-link"
                                                role="switch"
                                                aria-checked={pin.on}
                                                onClick={(e) => { e.stopPropagation(); pin.onToggle(!pin.on); }}
                                                title={pin.title ?? (pin.on
                                                    ? `"${c.label}" is shared across pages. Click to move it into the current page.`
                                                    : `Pin "${c.label}" — share it across all pages.`)}
                                                style={{
                                                    ...chrome.link,
                                                    cursor: 'pointer',
                                                    opacity: pin.on ? 1 : 0.7,
                                                    color: pin.on ? `rgb(${SELECTION})` : 'inherit',
                                                }}
                                            >
                                                <PinGlyph on={pin.on} />
                                            </span>
                                        )}
                                        <span className="pose-tab-label" style={chrome.tabLabel}>{c.label}</span>
                                    </span>
                                    {kids.length > 0 && (
                                        <span
                                            className="pose-ghost"
                                            onClick={(e) => {
                                                if (oneKid) adapter.onSelectObject?.(kids[0].object);
                                                else setCrumbMenu({ index: i, kind: 'children', x: e.clientX, y: e.clientY });
                                            }}
                                            title={oneKid ? `Select the child "${kids[0].label}"` : `"${c.label}" has ${kids.length} children — click to pick one`}
                                            style={{ ...chrome.ghost, '--ghost-fill': capturing ? 'rgba(20, 30, 46, 0.94)' : 'rgba(18, 20, 26, 0.94)' } as React.CSSProperties & Record<string, string>}
                                        >
                                            {oneKid
                                                ? <span style={chrome.ghostLabel}>{kids[0].label}</span>
                                                : <><b style={{ opacity: 0.85, fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{kids.length}</b> children</>}
                                        </span>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </div>
                ) : (
                    <span style={{ ...chrome.tab, ...chrome.tabFirst, color: INK, fontWeight: 600, cursor: 'default' }}>{ownerName}</span>
                )}
                {gated && (
                    <button
                        className="pose-close"
                        onClick={() => adapter.onToggleEnabled?.(false)}
                        title={`Close ${noun} editing — edits change the base scene`}
                        aria-label={`Close ${noun} editing`}
                        style={chrome.close}
                    >
                        ✕
                    </button>
                )}
            </div>

            <div style={{ display: 'flex', gap: '7px', overflowX: 'auto', padding: '1px', maxWidth: '100%' }}>
                {/* Base — authored scene, no pose applied. Adapters may rename
                    the card (pages call it "All": overview + base editing). */}
                <div
                    style={{
                        ...styles.card,
                        ...(active === null ? styles.cardSelected : {}),
                        ...(hovered === '\0base' && active !== null ? { transform: 'translateY(-1px)', borderColor: HAIRLINE_STRONG } : {}),
                    }}
                    onClick={() => selectPose(null)}
                    onMouseEnter={() => setHovered('\0base')}
                    onMouseLeave={() => setHovered(null)}
                    title={adapter.baseTitle ?? 'Base — authored scene with no pose applied. Edits change the scene itself.'}
                >
                    {thumb(null)
                        ? <img src={thumb(null)} alt={adapter.baseLabel ?? 'Base'} style={styles.cardImage} draggable={false} />
                        : <div style={{ ...styles.cardPlaceholder, color: `rgba(${CONTEXT}, 0.6)` }}>◇</div>}
                    {adapter.baseBadge !== null && <span style={styles.baseBadge}>{adapter.baseBadge ?? 'base'}</span>}
                    <div style={styles.cardLabel}>{adapter.baseLabel ?? 'Base'}</div>
                </div>

                {adapter.baseSeparated && (
                    <div style={{ width: '1px', minWidth: '1px', alignSelf: 'stretch', background: HAIRLINE_STRONG, borderRadius: '1px' }} />
                )}

                {names.map((name, index) => {
                    const isSelected = name === active;
                    const isHovered = name === hovered;
                    const isRenaming = name === renaming;
                    const t = thumb(name);
                    // Insertion indicator bars for card drag-reorder.
                    const bar = (side: 'left' | 'right'): React.CSSProperties => ({
                        position: 'absolute', top: 0, bottom: 0, width: '2px',
                        [side]: '-4.5px', backgroundColor: '#4a9eff', borderRadius: '1px', zIndex: 1,
                    });
                    return (
                        <div
                            key={name}
                            style={{ position: 'relative', display: 'flex', flexShrink: 0 }}
                            {...(canReorder ? getContainerProps(index) : {})}
                        >
                            {canReorder && dropSlot === index && <div style={bar('left')} />}
                            {canReorder && index === names.length - 1 && dropSlot === index + 1 && <div style={bar('right')} />}
                        <div
                            style={{
                                ...styles.card,
                                ...(isSelected ? styles.cardSelected : {}),
                                ...(isHovered && !isSelected ? { transform: 'translateY(-1px)', borderColor: HAIRLINE_STRONG } : {}),
                                ...(dragIndex === index ? { opacity: 0.45 } : {}),
                            }}
                            {...(canReorder && !isRenaming ? getDraggableProps(index) : {})}
                            onClick={() => !isRenaming && selectPose(name)}
                            onDoubleClick={(e) => { e.stopPropagation(); startRename(name); }}
                            onContextMenu={(e) => { if (isRenaming) return; e.preventDefault(); e.stopPropagation(); setMenu({ name, x: e.clientX, y: e.clientY }); }}
                            onMouseEnter={() => setHovered(name)}
                            onMouseLeave={() => setHovered(null)}
                            title={isRenaming ? '' : `${isSelected ? `"${name}" selected — edits store here` : `Select "${name}"`} (double-click to rename${canReorder ? ', drag to reorder' : ''})`}
                        >
                            {t
                                ? <img src={t} alt={name} style={styles.cardImage} draggable={false} />
                                : <div style={styles.cardPlaceholder}>◇</div>}
                            {isRenaming ? (
                                <input
                                    autoFocus
                                    style={styles.renameInput}
                                    value={draft}
                                    onChange={(e) => setDraft(e.target.value)}
                                    onBlur={() => commitRename(name)}
                                    onKeyDown={(e) => {
                                        e.stopPropagation();
                                        if (e.key === 'Enter') { e.preventDefault(); commitRename(name); }
                                        else if (e.key === 'Escape') { e.preventDefault(); setRenaming(null); }
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                />
                            ) : (
                                <div style={styles.cardLabel}>{name}</div>
                            )}
                            {!isRenaming && (
                                <button style={{ ...styles.deleteButton, opacity: isHovered ? 1 : 0 }} onClick={(e) => onDelete(name, e)} title={`Delete ${noun} "${name}"`}>×</button>
                            )}
                        </div>
                        </div>
                    );
                })}

                <button
                    className="pose-add"
                    style={styles.addCard}
                    onClick={onAdd}
                    title={`Add a new ${noun} and select it`}
                >+</button>
            </div>

            {/* Footer — the coordinator's persistent tab bar sits inside the
                panel so its background encompasses the tabs. */}
            {adapter.footer && <div style={{ display: 'flex' }}>{adapter.footer}</div>}

            {menu && createPortal(
                <div
                    ref={menuRef}
                    className="viewport-context-menu"
                    style={{ left: menuPos?.left ?? menu.x, top: menuPos?.top ?? menu.y, visibility: menuPos ? 'visible' : 'hidden' }}
                    onContextMenu={(e) => e.preventDefault()}
                >
                    <button onClick={() => { onDuplicate(menu.name); setMenu(null); }}>Duplicate</button>
                    <button onClick={() => { startRename(menu.name); setMenu(null); }}>Rename</button>
                    <button onClick={(e) => { onDelete(menu.name, e); setMenu(null); }}>Delete</button>
                </div>,
                document.body,
            )}

            {/* Breadcrumb hop menu — siblings of a crumb, or children of the leaf. */}
            {crumbMenu && createPortal(
                <div
                    ref={crumbMenuRef}
                    className="viewport-context-menu"
                    style={{ left: crumbMenuPos?.left ?? crumbMenu.x, top: crumbMenuPos?.top ?? crumbMenu.y, visibility: crumbMenuPos ? 'visible' : 'hidden' }}
                    onContextMenu={(e) => e.preventDefault()}
                >
                    {(adapter.breadcrumb?.[crumbMenu.index]?.[crumbMenu.kind] ?? []).map((s, j) => (
                        <button
                            key={j}
                            onClick={() => { adapter.onSelectObject?.(s.object); setCrumbMenu(null); }}
                            style={s.pin ? { display: 'flex', alignItems: 'center', gap: '8px', width: '100%' } : undefined}
                        >
                            {s.pin ? <span style={{ flex: 1, textAlign: 'left' }}>{s.label}</span> : s.label}
                            {s.pin && (
                                <span
                                    role="switch"
                                    aria-checked={s.pin.on}
                                    onClick={(e) => { e.stopPropagation(); s.pin!.onToggle(); setCrumbMenu(null); }}
                                    title={s.pin.title}
                                    style={{
                                        display: 'inline-flex', alignItems: 'center', padding: '2px',
                                        color: s.pin.on ? `rgb(${SELECTION})` : INK_FAINT,
                                    }}
                                >
                                    <PinGlyph on={s.pin.on} />
                                </span>
                            )}
                        </button>
                    ))}
                </div>,
                document.body,
            )}
            </div>
        </>
    );
}

// ── Mount logic (generic; from mountKeyframeContext) ─────────

export interface PoseStripControls {
    /** Re-render with a new adapter (or null to hide). Re-keys on owner change. */
    render: (adapter: PoseAdapter | null) => void;
    dispose: () => void;
}

/**
 * Mount a PoseStrip into `containerId`, positioning it between the inspector
 * panes via a ResizeObserver (same as the keyframe overlay). The caller resolves
 * the adapter from the current selection and calls `render`.
 */
export function mountPoseStrip(containerId: string): PoseStripControls {
    const el = document.getElementById(containerId);
    if (!el) return { render() {}, dispose() {} };
    const container = el;

    let root: Root | null = null;
    let disposePaneObserver: (() => void) | null = null;

    function setupPaneObserver() {
        disposePaneObserver?.();
        disposePaneObserver = observeInspectorPaneInsets(({ left, right }) => {
            container.style.left = `${left}px`;
            container.style.right = `${right}px`;
        });
    }

    function render(adapter: PoseAdapter | null) {
        root ??= createRoot(container);
        if (!adapter) { root.render(null); return; }
        root.render(<PoseStrip key={adapter.owner.transform.uniqueId} adapter={adapter} />);
        requestAnimationFrame(() => setupPaneObserver());
    }

    return {
        render,
        dispose: () => {
            disposePaneObserver?.();
            disposePaneObserver = null;
            root?.unmount();
            root = null;
        },
    };
}
